import React from "react";
import { clearAuthToken } from "@/lib/auth";

type ErrorBoundaryState = {
  hasError: boolean;
  message: string;
  reauth: boolean;
};

function errorText(error: unknown) {
  if (error instanceof Error) return `${error.name} ${error.message} ${error.stack || ""}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error || "");
  }
}

function isDynamicImportError(error: unknown) {
  return /Failed to fetch dynamically imported module|dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [\w-]+ failed|CSS_CHUNK_LOAD_FAILED|Failed to load module script/i.test(errorText(error));
}

function redirectToLoginForFreshBundle() {
  if (typeof window === "undefined") return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const params = new URLSearchParams({
    reason: "chunk-expired",
    t: String(Date.now())
  });
  if (current && !current.startsWith("/login")) params.set("redirect", current);
  clearAuthToken();
  window.location.replace(`/login?${params.toString()}`);
}

export default class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, message: "", reauth: false };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : "页面组件渲染异常",
      reauth: isDynamicImportError(error)
    };
  }

  componentDidMount() {
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
    window.addEventListener("vite:preloadError", this.handleVitePreloadError as EventListener);
    window.addEventListener("error", this.handleWindowError);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
    window.removeEventListener("vite:preloadError", this.handleVitePreloadError as EventListener);
    window.removeEventListener("error", this.handleWindowError);
  }

  componentDidCatch(error: unknown) {
    console.error("UI render failed", error);
    if (isDynamicImportError(error)) {
      window.setTimeout(redirectToLoginForFreshBundle, 30);
    }
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (!isDynamicImportError(event.reason)) return;
    event.preventDefault();
    redirectToLoginForFreshBundle();
  };

  handleVitePreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload || event;
    if (!isDynamicImportError(payload)) return;
    event.preventDefault();
    redirectToLoginForFreshBundle();
  };

  handleWindowError = (event: ErrorEvent) => {
    if (!isDynamicImportError(event.error || event.message || event.filename)) return;
    event.preventDefault();
    redirectToLoginForFreshBundle();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.reauth) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--background)" }}>
          <div
            className="w-full max-w-md bg-white rounded-xl p-6 text-center"
            style={{ border: "1px solid var(--border)", boxShadow: "0 10px 30px rgba(15,23,42,0.08)" }}
          >
            <div className="text-lg font-bold mb-2" style={{ color: "var(--foreground)" }}>正在重新进入登录</div>
            <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>页面版本已更新，请重新登录后继续操作。</div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--background)" }}>
        <div
          className="w-full max-w-md bg-white rounded-xl p-6 text-center"
          style={{ border: "1px solid var(--border)", boxShadow: "0 10px 30px rgba(15,23,42,0.08)" }}
        >
          <div className="text-lg font-bold mb-2" style={{ color: "var(--foreground)" }}>页面加载异常</div>
          <div className="text-sm mb-4" style={{ color: "var(--muted-foreground)" }}>{this.state.message}</div>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, message: "", reauth: false })}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg, oklch(0.62 0.18 35), oklch(0.68 0.16 45))" }}
          >
            返回页面
          </button>
        </div>
      </div>
    );
  }
}
